import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, withModifiers as _withModifiers, openBlock as _openBlock, createBlock as _createBlock } from "vue"

const _hoisted_1 = ["onSubmit"]
const _hoisted_2 = { class: "text-center font-medium dark:text-white" }
const _hoisted_3 = { class: "space-y-2" }

import { Contact, ContactPayload } from "@toruslabs/base-controllers";
import { Button, Select, TextField } from "@toruslabs/vue-components";
import useVuelidate from "@vuelidate/core";
import { helpers, required } from "@vuelidate/validators";
import { reactive } from "vue";
import { useI18n } from "vue-i18n";
import { isValidAddress } from "xrpl";

import useCustomValidators from "@/customValidators";
import { addToast } from "@/modules/app";
import ControllerModule from "@/modules/controllers";
import { ALLOWED_VERIFIERS, ALLOWED_VERIFIERS_ERRORS, STATUS, TransferType } from "@/utils/enums";

import Modal from "./Modal.vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'AddContactForm',
  props: {
    open: { type: Boolean, default: false },
    contacts: {}
  },
  emits: ["onCloseModal"],
  setup(__props: any, { emit: emits }) {

const props = __props;

const { t } = useI18n();

const { validateVerifierId } = useCustomValidators();

const transferTypes = ALLOWED_VERIFIERS.map((type) => ({
  ...type,
  name: t(type.name),
}));



const newContactState = reactive<{
  name: string;
  address: string;
  transferType: TransferType;
}>({
  name: "",
  address: "",
  transferType: ALLOWED_VERIFIERS[0],
});

const validVerifier = (value: string) => {
  return validateVerifierId(newContactState.transferType.value, value);
};

const getVerifierErrorMessage = () => {
  const selectedType = newContactState.transferType || "";
  if (!selectedType) return "";
  return t(ALLOWED_VERIFIERS_ERRORS[selectedType.value]);
};

const validNewContact = (value: string): boolean => {
  return !props.contacts.some((x) => x.contact_verifier_id.toLowerCase() === value.toLowerCase());
};

const rules = {
  name: {
    required: helpers.withMessage(t("common.required"), required),
    alphanumericAndSpace: helpers.withMessage("Name can contain alphanumeric or spaces only", (value: string) => /^[a-zA-Z0-9\s]+$/.test(value)),
  },
  address: {
    required: helpers.withMessage(t("common.required"), required),
    validTransferTo: helpers.withMessage(getVerifierErrorMessage, (value: string) => validVerifier(value)),
    isDuplicateContact: helpers.withMessage(t("walletSettings.duplicateContact"), (value: string) => validNewContact(value)),
  },
};

const $v = useVuelidate(rules, newContactState);

const closeModal = () => {
  emits("onCloseModal");

  Object.assign(newContactState, {
    name: "",
    address: "",
    transferType: ALLOWED_VERIFIERS[0],
  });
  $v.value.$reset();
};

const onSave = async () => {
  $v.value.$touch();

  if ($v.value.$invalid) {
    return;
  }

  if (!isValidAddress(newContactState.address)) {
    addToast({
      message: t("walletSettings.invalidXrpl"),
      type: STATUS.DANGER,
    });
    return;
  }

  if (
    ControllerModule.contacts.find(
      (contact) => contact.contact_verifier_id === newContactState.address || contact.display_name === newContactState.name
    )
  ) {
    addToast({
      message: t("walletSettings.addressAlreadyExistAddContact"),
      type: STATUS.DANGER,
    });
    return;
  }

  const contactPayload: ContactPayload = {
    display_name: newContactState.name,
    contact_verifier_id: newContactState.address,
    contact_verifier: newContactState.transferType.value,
  };

  await ControllerModule.addContact(contactPayload);
  closeModal();
};

const setTransferType = (newTransferType: TransferType) => {
  newContactState.transferType = newTransferType;
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(Modal, {
    open: _ctx.open,
    onOnCloseModal: closeModal
  }, {
    default: _withCtx(() => [
      _createElementVNode("form", {
        class: "space-y-6",
        onSubmit: _withModifiers(onSave, ["prevent"])
      }, [
        _createElementVNode("div", _hoisted_2, _toDisplayString(_unref(t)("walletSettings.addNewContact")), 1),
        _createVNode(_unref(TextField), {
          modelValue: newContactState.name,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((newContactState.name) = $event)),
          label: _unref(t)('walletSettings.name'),
          classes: { label: '!text-xs !text-app-gray-500 dark:!text-app-gray-400' },
          placeholder: _unref(t)('walletSettings.namePlaceholder'),
          pill: "",
          error: _unref($v).name.$error,
          "helper-text": _unref($v).name.$error ? _unref($v).name.$errors[0].$message.toString() : ''
        }, null, 8, ["modelValue", "label", "placeholder", "error", "helper-text"]),
        _createElementVNode("div", _hoisted_3, [
          _createVNode(_unref(Select), {
            disabled: true,
            "model-value": newContactState.transferType.value,
            "input-props": { pill: true },
            options: _unref(transferTypes),
            "show-clear-all-icon": false,
            label: _unref(t)('walletSettings.address'),
            "input-classes": { label: '!text-xs !text-app-gray-500 dark:!text-app-gray-400' },
            "aria-labelledby": _unref(t)('walletSettings.address'),
            onOnSelect: setTransferType
          }, null, 8, ["model-value", "options", "label", "aria-labelledby"]),
          _createVNode(_unref(TextField), {
            modelValue: newContactState.address,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((newContactState.address) = $event)),
            pill: "",
            placeholder: _unref(t)('walletSettings.addressPlaceholder', [_unref(t)(newContactState.transferType.name)]),
            error: _unref($v).address.$error,
            "helper-text": _unref($v).address.$error ? _unref($v).address.$errors[0].$message.toString() : ''
          }, null, 8, ["modelValue", "placeholder", "error", "helper-text"])
        ]),
        _createElementVNode("div", null, [
          _createVNode(_unref(Button), {
            class: "mb-5",
            pill: "",
            block: "",
            type: "submit"
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_unref(t)("walletSettings.addContact")), 1)
            ]),
            _: 1
          }),
          _createVNode(_unref(Button), {
            variant: "text",
            class: "!text-base dark:text-app-gray-400",
            block: "",
            onClick: closeModal
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_unref(t)("common.cancel")), 1)
            ]),
            _: 1
          })
        ])
      ], 40, _hoisted_1)
    ]),
    _: 1
  }, 8, ["open"]))
}
}

})